// 🦷 치과 설정 — 「환자 공개 범위」 (2026-10-14 대표 승인, 플래그 clinicPatientShareUi strict, 배포 전 OFF)
// 계획 docs/착수계획_보철제작기록_2026-10-14.md §2-2·§4-1(웹 행)
//  - 3스위치: 재료 보이기(showMaterial) · 기공소명 보이기(showLabName) · 디자인 완료 알림(notifyDesignDone)
//  - 조회·저장 = CF(getPatientShare·setPatientShare)만 — services/patientShareApi.js 한 곳. Firestore 직접 쓰기 금지(rules 차단)
//  - 치과 **대표만** 변경(서버가 최종 판정 owner-only). 직원은 같은 값 보기만
//  - 문구 = 법적보안 정본(docs/법무검토_보철제작기록P1문구_2026-10-14.md §2). 환자 노출 금지어 사용 금지
import React, { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import PageHeader from '../common/PageHeader';
import { getFeatures, isClinicPatientShareUiOn } from '../../utils/features';
import { readPatientShare, savePatientShare, normalizeShare, describePatientShareError } from '../../services/patientShareApi';

// 문구 정본: docs/법무검토_보철제작기록P1문구_2026-10-14.md §2 표 글자 그대로 (2026-10-14 법적보안). 바꾸려면 정본부터.
// 미리보기 문자열은 §4 A+B 금지어 0 (치과용 설명 문장 제외). 고정 문장 = 정본 §1-2 recordNotice(치과명 치환, 앱과 동일)
const TITLE = '환자 공개 범위';
const HELP = '본원 환자가 덴클 케어 앱의 「보철 제작 기록」에서 볼 내용을 정합니다. 대표 계정만 바꿀 수 있고, 바꾼 사람과 시각이 기록됩니다.';
const COMMON_NOTE = '환자가 이미 사진 앱에 저장한 이미지는 설정을 바꿔도 바뀌지 않습니다.';
const ITEMS = [
    { key: 'showMaterial', label: '재료 보이기', desc: '켜면 의뢰서에 적힌 보철 재료가 환자 앱에 표시됩니다. 이미 연결된 의뢰서는 그 의뢰서의 진행 정보가 다음에 바뀔 때부터 표시됩니다. 끄면 바로 표시되지 않습니다.' },
    { key: 'showLabName', label: '기공소명 보이기', desc: '켜면 보철물을 만든 치과기공소 이름이 표시됩니다. 기공소가 이름 공개를 원하지 않으면 켜도 표시되지 않습니다. 기공소의 연락처·주소는 표시되지 않습니다.' },
    { key: 'notifyDesignDone', label: '디자인 완료 알림', desc: "켜면 기공소가 디자인을 마쳤을 때 본원 이름으로 환자 앱에 알림을 보냅니다. 끄더라도 앱의 제작 과정에는 '디자인 완료' 단계가 그대로 보입니다." },
];
// 환자 화면 미리보기 (예시 값) — 정본 §2 미리보기 본문
const PREVIEW_TITLE = '환자 화면 미리보기 (예시 값)';
const PREVIEW_CARD_TITLE = '보철 제작 기록';
const PREVIEW_ROWS = [
    { label: '치식', value: '46' },   // 대표 10-14 「치아 위치」→「치식」(FDI 번호, 앱과 같음)
    { label: '보철 종류', value: '크라운' },
    { label: '재료', value: '지르코니아', shareKey: 'showMaterial' },
    { label: '제작 기공소', value: '○○치과기공소', shareKey: 'showLabName' },
    { label: '의뢰일', value: '2026년 10월 1일' },
    { label: '제작일(포장)', value: '2026년 10월 8일' },
];
// 대표 10-14 간소화 + 법적보안 확인 — 앱 RecordTexts.fixedNotice 와 글자 같게
const recordNotice = (clinicName) => `${clinicName}이(가) 보낸 의뢰서와 기공소의 제작 진행 내용으로, 실제와 다를 수 있으며 진료기록이 아닙니다. 문의는 ${clinicName}에 해 주세요.`;

function fmtDate(d) {
    if (!d) return '';
    return `${d.getFullYear()}.${d.getMonth() + 1}.${d.getDate()} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function ClinicPatientShareSettings({ user }) {
    // 훅은 전부 최상단 — early return 위 (Dashboard 사고)
    const isStaff = user?.userType === 'staff' || !!user?.companyId;
    const clinicUid = user?.companyId || user?.uid;
    const businessType = user?.businessType || user?.companyBusinessType;
    const isClinic = businessType === 'clinic' || businessType === 'dental' || businessType === '치과';
    const clinicName = user?.businessName || user?.companyName || user?.name || '○○치과';   // 미리보기 치환용 — 서버 환자 응답 치과명과 같은 순서(functions/lib/clinic-patients.js clinicName)

    const [flagOn, setFlagOn] = useState(null);     // null=로딩
    const [share, setShare] = useState(null);       // null=로딩
    const [last, setLast] = useState(null);
    const [loadErr, setLoadErr] = useState('');
    const [busyKey, setBusyKey] = useState('');
    const [notice, setNotice] = useState(null);     // { type:'ok'|'err', text }

    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFlagOn(isClinicPatientShareUiOn(f)); })
            .catch((e) => { console.error('[환자 공개 범위] 플래그 조회 실패:', e?.message || e); if (alive) setFlagOn(false); });
        return () => { alive = false; };
    }, [user]);

    useEffect(() => {
        if (!flagOn || !clinicUid || !isClinic) return;
        let alive = true;
        readPatientShare(clinicUid)
            .then(r => { if (alive) { setShare(r.share); setLast(r.last); setLoadErr(''); } })
            .catch((e) => {
                // 조회 실패 시 값을 지어내지 않는다 — 스위치 잠그고 이유 표시
                if (alive) setLoadErr(describePatientShareError(e, '현재 설정을 불러오지 못했습니다. 새로고침해 주세요.'));
            });
        return () => { alive = false; };
    }, [flagOn, clinicUid, isClinic]);

    if (!isClinic) {
        return (
            <div style={S.container}>
                <PageHeader icon={Eye} title={TITLE} />
                <div style={S.emptyBox}>치과 계정만 사용할 수 있는 화면입니다.</div>
            </div>
        );
    }
    if (flagOn === false) {
        return (
            <div style={S.container}>
                <PageHeader icon={Eye} title={TITLE} />
                <div style={S.emptyBox}>환자 공개 범위 기능이 아직 열리지 않았습니다.</div>
            </div>
        );
    }

    const toggle = async (key) => {
        if (isStaff || busyKey || !share) return;
        const next = normalizeShare({ ...share, [key]: !share[key] });
        setBusyKey(key); setNotice(null);
        try {
            await savePatientShare(next);
            setShare(next);
            // 최근 변경 줄 갱신 — 다시 읽기 실패해도 저장은 끝났으므로 값만 유지하고 사유는 기록
            try {
                const r = await readPatientShare(clinicUid);
                setShare(r.share); setLast(r.last);
            } catch (e2) {
                console.error('[환자 공개 범위] 저장 후 다시 읽기 실패:', e2?.code || e2?.message || e2);
                setLast({ at: new Date(), byText: '대표 계정' });
            }
            setNotice({ type: 'ok', text: '저장했습니다.' });
        } catch (e) {
            setNotice({ type: 'err', text: describePatientShareError(e, '저장하지 못했습니다. 잠시 후 다시 시도해 주세요.') });
        } finally { setBusyKey(''); }
    };

    const loading = flagOn === null || (share === null && !loadErr);

    return (
        <div style={S.container}>
            <PageHeader icon={Eye} title={TITLE} />
            <div style={S.box}>
                <div style={S.desc}>{HELP}</div>
                {isStaff && <div style={S.readonly}>직원 계정은 확인만 할 수 있습니다. 변경은 치과 대표 계정에서 합니다.</div>}
                {loading ? (
                    <div style={S.help}>불러오는 중…</div>
                ) : share && (
                    <div style={S.list}>
                        {ITEMS.map(it => {
                            const on = share[it.key] === true;
                            const disabled = isStaff || !!busyKey;
                            return (
                                <div key={it.key} style={S.item}>
                                    <div style={S.row}>
                                        <span style={S.itemLabel}>{it.label}</span>
                                        <button type="button" role="switch" aria-checked={on} aria-label={it.label} disabled={disabled} onClick={() => toggle(it.key)}
                                            style={{ ...S.switch, background: on ? '#2563eb' : '#cbd5e1', cursor: disabled ? 'default' : 'pointer', opacity: busyKey === it.key ? 0.6 : 1 }}>
                                            <span style={{ ...S.knob, transform: on ? 'translateX(20px)' : 'translateX(0)' }} />
                                        </button>
                                        <span style={{ ...S.state, color: on ? '#1d4ed8' : '#94a3b8' }}>{on ? '켜짐' : '꺼짐'}</span>
                                    </div>
                                    <div style={S.sub}>{it.desc}</div>
                                </div>
                            );
                        })}
                    </div>
                )}
                {share && (
                    <div style={S.last}>
                        {last
                            ? `최근 변경: ${fmtDate(last.at) || '시각 미상'}${last.byText ? ` · ${last.byText}` : ''}`
                            : '최근 변경: 기록 없음'}
                    </div>
                )}
                <div style={S.help}>{COMMON_NOTE}</div>
                {loadErr && <div style={S.err}>{loadErr}</div>}
                {notice && <div style={notice.type === 'ok' ? S.ok : S.err}>{notice.text}</div>}
            </div>

            {share && (
                <div style={S.box}>
                    <div style={S.sectionTitle}>{PREVIEW_TITLE}</div>
                    <div style={S.pvCard}>
                        <div style={S.pvTitle}>{PREVIEW_CARD_TITLE}</div>
                        {PREVIEW_ROWS.filter(r => !r.shareKey || share[r.shareKey] === true).map(r => (
                            <div key={r.label} style={S.pvRow}>
                                <span style={S.pvLabel}>{r.label}</span>
                                <span style={S.pvValue}>{r.value}</span>
                            </div>
                        ))}
                        <div style={S.pvNotice}>{recordNotice(clinicName)}</div>
                    </div>
                    {/* 꺼진 항목은 미리보기에서 행을 빼고 카드 밖에 1줄 (치과용 설명 — 정본 §2) */}
                    {PREVIEW_ROWS.filter(r => r.shareKey && share[r.shareKey] !== true).map(r => (
                        <div key={r.label} style={S.pvOff}>{`${r.label}: 환자에게 보이지 않음`}</div>
                    ))}
                </div>
            )}
        </div>
    );
}

const S = {
    container: { padding: 20, maxWidth: 720, margin: '0 auto' },
    emptyBox: { border: '1px solid #e2e8f0', borderRadius: 12, padding: 26, textAlign: 'center', fontSize: 14, color: '#9ca3af', background: '#fff' },
    box: { background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: '16px 18px', marginBottom: 14 },
    sectionTitle: { fontSize: 14.5, fontWeight: 700, color: '#0f172a', marginBottom: 10 },
    help: { fontSize: 12.5, color: '#64748b', lineHeight: 1.6, marginTop: 10 },
    readonly: { fontSize: 12.5, color: '#92400e', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '8px 11px', marginBottom: 10 },
    list: { display: 'flex', flexDirection: 'column', gap: 4 },
    item: { padding: '9px 4px', borderBottom: '1px solid #f1f5f9' },
    row: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
    itemLabel: { fontSize: 13.5, fontWeight: 600, color: '#0f172a', minWidth: 110 },
    switch: { position: 'relative', width: 44, height: 24, borderRadius: 999, border: 'none', padding: 0, flexShrink: 0, transition: 'background .15s' },
    knob: { position: 'absolute', top: 2, left: 2, width: 20, height: 20, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 2px rgba(0,0,0,0.25)', transition: 'transform .15s' },
    state: { fontSize: 12, fontWeight: 600 },
    sub: { fontSize: 12, color: '#94a3b8', marginTop: 4, lineHeight: 1.5 },
    last: { fontSize: 12, color: '#475569', marginTop: 10 },
    desc: { fontSize: 13, color: '#334155', lineHeight: 1.6, marginBottom: 12 },
    pvCard: { border: '1px solid #e2e8f0', borderRadius: 12, padding: '14px 16px', background: '#f8fafc', maxWidth: 360 },
    pvTitle: { fontSize: 14.5, fontWeight: 700, color: '#0f172a', marginBottom: 8 },
    pvRow: { display: 'flex', gap: 12, fontSize: 13, padding: '4px 0' },
    pvLabel: { color: '#64748b', minWidth: 84 },
    pvValue: { color: '#0f172a', fontWeight: 600 },
    pvNotice: { fontSize: 12, color: '#475569', lineHeight: 1.6, marginTop: 10, paddingTop: 10, borderTop: '1px solid #e2e8f0' },
    pvOff: { fontSize: 12.5, color: '#64748b', marginTop: 8 },
    ok: { marginTop: 10, background: '#ecfdf5', border: '1px solid #a7f3d0', color: '#065f46', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
    err: { marginTop: 10, background: '#fdecec', border: '1px solid #f5cfcf', color: '#8a2020', borderRadius: 8, padding: '8px 11px', fontSize: 12.5 },
};

export default ClinicPatientShareSettings;
